import React, { useState, Suspense, lazy, useEffect } from "react";
import { Routes, Route, NavLink, Navigate, Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Shield, Brain, Database, Cpu, Network, Terminal,
  Activity, Zap, Eye, ChevronLeft, ChevronRight, LogOut, FlaskConical,
  Siren, Loader2, LayoutDashboard, GitBranch, Box, Bug, BadgeCheck,
  History, Menu, X, Sliders, UserCircle, Radar, SlidersHorizontal, Scale,
  UserCheck, Users, Gauge, Filter, UserCog, Sparkles,
} from "lucide-react";

import { AuthProvider, useAuth } from "./auth/AuthContext";
import ProtectedRoute from "./auth/ProtectedRoute";
import Login from "./auth/Login";
import Profile from "./auth/Profile";
import ScopeSelector from "./auth/ScopeSelector";
import RelativeTime from "./lib/relative-time";
import { scopeKeyOf } from "./lib/scope";
import LanguageSwitcher from "./components/LanguageSwitcher";

// ── Module imports — lazy-loaded ────────────────────────────────────────────
// Chaque module est chargé uniquement quand sa route est naviguée.
const Overview                 = lazy(() => import("./modules/overview"));
const ADNModule                = lazy(() => import("./modules/adn"));
const FlowsModule              = lazy(() => import("./modules/flows"));
const IncidentsModule          = lazy(() => import("./modules/incidents"));
const NoveltyCandidatesModule  = lazy(() => import("./modules/novelty-candidates"));
const CampaignsModule          = lazy(() => import("./modules/campaigns"));
const ModelHealthModule        = lazy(() => import("./modules/model-health"));
const CostBudgetModule         = lazy(() => import("./modules/cost-budget"));
const SuppressionSuggestionsModule = lazy(() => import("./modules/suppression-suggestions"));
const ArbiterModule = lazy(() => import("./modules/arbiter"));
const HoneyDataModule          = lazy(() => import("./modules/honey-data"));
const ShadowDashboard          = lazy(() => import("./modules/shadow-dashboard"));
const Models                   = lazy(() => import("./modules/models"));
const WarModeModule            = lazy(() => import("./modules/war-mode"));
const Console3DModule          = lazy(() => import("./modules/console-3d"));
const PostQuantumModule        = lazy(() => import("./modules/post-quantum"));
const SBOMModule               = lazy(() => import("./modules/sbom"));
const FederatedLearningModule  = lazy(() => import("./modules/federated-learning"));
const LSTMGNNWasmModule        = lazy(() => import("./modules/lstm-gnn-wasm"));
const RedTeamModule            = lazy(() => import("./modules/red-team"));
const EntropyAnalyzerModule    = lazy(() => import("./modules/entropy-analyzer"));
const TrustedSourcesModule     = lazy(() => import("./modules/trusted-sources"));
const BaselinesModule          = lazy(() => import("./modules/baselines"));
const FamilyThresholdsModule   = lazy(() => import("./modules/family-thresholds"));
const FleetShiftsModule        = lazy(() => import("./modules/fleet-shifts"));
const HostBaselinePolicyModule = lazy(() => import("./modules/host-baseline-policy"));
const JudgeConfigModule        = lazy(() => import("./modules/judge-config"));
const ProbeSetupModule         = lazy(() => import("./modules/probe-setup"));
const ShadowReviewModule       = lazy(() => import("./modules/shadow-review"));
const UsersModule              = lazy(() => import("./modules/users"));
// Vue détail d'une IP — pas d'entrée de nav : on y arrive en cliquant sur
// n'importe quelle IP de l'interface (`<IpLink>`, cf. `lib/ip.jsx`).
const IpDetailModule           = lazy(() => import("./modules/ip-detail"));

function ModuleSpinner() {
  const { t } = useTranslation("common");
  return (
    <div className="h-full flex items-center justify-center gap-2" style={{ color: "#64748b" }}>
      <Loader2 size={18} className="animate-spin" />
      <span className="text-xs">{t("loading_module")}</span>
    </div>
  );
}

// 3 sections — distinguent l'usage quotidien (Live Ops) de la gouvernance
// (configurable mais peu fréquente) et des outils de démo/lab. Les
// libellés sont des clés i18n (namespace `nav`) résolues côté Sidebar
// pour rester compatibles avec un changement de langue runtime.
const NAV_SECTIONS = [
  {
    labelKey: "live_ops",
    items: [
      { path: "/",          labelKey: "overview",     icon: LayoutDashboard, color: "#00d4ff" },
      // Les 3 grains de la détection, contigus et dans l'ordre croissant :
      // un verdict par flux → un épisode par source → un cluster de sources.
      // Les libellés portent le grain — « Anomalies » et « Incidents » côte à
      // côte se lisent sinon comme une gradation de sévérité, alors que ce sont
      // deux échelles d'observation du même trafic.
      { path: "/anomalies", labelKey: "anomalies",    icon: Shield,          color: "#00d4ff" },
      { path: "/incidents", labelKey: "incidents",    icon: Siren,           color: "#ef4444" },
      { path: "/campaigns", labelKey: "campaigns",    icon: Users,           color: "#d946ef" },
      { path: "/flows",     labelKey: "flows",        icon: Activity,        color: "#22d3ee" },
      { path: "/novelty-candidates", labelKey: "novelty_candidates", icon: Sparkles, color: "#a78bfa" },
      { path: "/topology",  labelKey: "topology",     icon: Network,         color: "#60a5fa" },
      { path: "/shadow",    labelKey: "shadow_mode",  icon: Eye,             color: "#94a3b8" },
    ],
  },
  {
    labelKey: "governance",
    items: [
      { path: "/honey-data", labelKey: "honey_traps", icon: Database,    color: "#a78bfa" },
      { path: "/war-mode",   labelKey: "war_mode",    icon: Siren,       color: "#ef4444" },
      { path: "/models",     labelKey: "models",      icon: GitBranch,   color: "#34d399" },
      { path: "/trusted",    labelKey: "trusted",     icon: BadgeCheck,  color: "#22c55e" },
      { path: "/baselines",  labelKey: "baselines",   icon: History,     color: "#60a5fa" },
      { path: "/family-thresholds", labelKey: "thresholds", icon: Sliders, color: "#a78bfa" },
      { path: "/fleet-shifts", labelKey: "fleet_shifts", icon: Radar, color: "#d946ef" },
      { path: "/host-baseline-policy", labelKey: "host_baseline_policy", icon: SlidersHorizontal, color: "#34d399" },
      { path: "/judge-config", labelKey: "judge_config", icon: Scale, color: "#38bdf8" },
      { path: "/shadow-review", labelKey: "shadow_review", icon: UserCheck, color: "#94a3b8" },
      { path: "/arbiter", labelKey: "arbiter", icon: Scale, color: "#38bdf8" },
      { path: "/probe-setup", labelKey: "probe_setup", icon: Terminal, color: "#22d3ee",
        requireRoles: ["platform_admin", "mssp_admin"] },
      { path: "/users", labelKey: "users", icon: UserCog, color: "#22d3ee",
        requireRoles: ["tenant_admin"] },
    ],
  },
  {
    labelKey: "lab",
    items: [
      { path: "/post-quantum", labelKey: "post_quantum",   icon: Zap,           color: "#34d399" },
      { path: "/lstm-gnn",     labelKey: "wasm_inference", icon: Brain,         color: "#e879f9" },
      { path: "/entropy",      labelKey: "entropy",        icon: FlaskConical,  color: "#f59e0b" },
      { path: "/red-team",     labelKey: "red_team",       icon: Cpu,           color: "#fb923c" },
      { path: "/sbom",         labelKey: "sbom",           icon: Box,           color: "#fbbf24" },
      { path: "/federated",    labelKey: "federated",      icon: Network,       color: "#f87171" },
      { path: "/model-health", labelKey: "model_health",   icon: Activity,      color: "#34d399" },
      { path: "/cost-budget",  labelKey: "cost_budget",    icon: Gauge,         color: "#f59e0b" },
      { path: "/suppression-suggestions", labelKey: "suppression_suggestions", icon: Filter, color: "#94a3b8" },
    ],
  },
];

function Sidebar({ collapsed, onToggle, mobileOpen, onMobileClose }) {
  const { t } = useTranslation(["nav", "auth"]);
  const { user } = useAuth();
  // Sur mobile (< md), la sidebar devient un drawer overlay piloté par
  // `mobileOpen`. Sur desktop (≥ md), c'est la sidebar inline historique
  // pilotée par `collapsed` (220px ↔ 64px).
  const onLinkClick = () => {
    if (mobileOpen) onMobileClose();
  };

  return (
    <>
      {/* Backdrop mobile */}
      {mobileOpen && (
        <button
          aria-label={t("auth:status.close_navigation")}
          onClick={onMobileClose}
          className="fixed inset-0 z-30 bg-black/60 md:hidden"
        />
      )}

      <aside
        className={`flex flex-col border-r transition-all duration-300 z-40
          fixed inset-y-0 left-0 md:static md:translate-x-0
          ${mobileOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"}
        `}
        style={{
          width: collapsed ? "64px" : "220px",
          background: "var(--color-surface)",
          borderColor: "var(--color-border)",
          flexShrink: 0,
          height: "100vh",
        }}
      >
        <div
          className="flex items-center gap-3 px-4 py-5 border-b"
          style={{ borderColor: "var(--color-border)", minHeight: "64px" }}
        >
          <Shield size={24} color="#00d4ff" className="shrink-0" />
          {!collapsed && (
            <span className="font-bold text-sm tracking-widest flex-1" style={{ color: "#00d4ff" }}>
              Ward³
            </span>
          )}
          {/* Close button — mobile only */}
          <button
            onClick={onMobileClose}
            aria-label={t("auth:status.close_navigation")}
            className="md:hidden p-1 -mr-1 rounded hover:bg-white/5"
            style={{ color: "var(--color-text-muted)" }}
          >
            <X size={18} />
          </button>
        </div>

        <nav className="flex-1 py-2 overflow-y-auto">
          {NAV_SECTIONS.map((section) => (
            <div key={section.labelKey} className="mb-2">
              {!collapsed && (
                <div
                  className="px-4 pt-3 pb-1 text-[10px] uppercase tracking-widest"
                  style={{ color: "var(--color-text-muted)" }}
                >
                  {t(section.labelKey)}
                </div>
              )}
              {section.items
                .filter(
                  (it) =>
                    !it.requireRoles ||
                    (user && it.requireRoles.includes(user.role_v2)),
                )
                .map(({ path, labelKey, icon: Icon, color }) => {
                const label = t(labelKey);
                return (
                  <NavLink
                    key={path}
                    to={path}
                    end={path === "/"}
                    onClick={onLinkClick}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-4 py-2 text-sm transition-colors ${
                        isActive ? "border-r-2" : "hover:bg-white/5"
                      }`
                    }
                    style={({ isActive }) => ({
                      borderColor: isActive ? color : "transparent",
                      background: isActive ? `${color}15` : undefined,
                      color: isActive ? color : "var(--color-text-muted)",
                    })}
                    /* Toujours porté : les libellés qui nomment leur grain
                       (« Campagnes · clusters ») peuvent être tronqués par la
                       sidebar étendue, où il n'y avait jusqu'ici aucun recours. */
                    title={label}
                  >
                    <Icon size={16} className="shrink-0" />
                    {!collapsed && <span className="truncate">{label}</span>}
                  </NavLink>
                );
              })}
            </div>
          ))}
        </nav>

        <button
          onClick={onToggle}
          aria-label={collapsed ? t("auth:status.expand_sidebar") : t("auth:status.collapse_sidebar")}
          data-testid="sidebar-toggle"
          className="hidden md:flex items-center justify-center py-3 border-t hover:bg-white/5 transition-colors"
          style={{ borderColor: "var(--color-border)", color: "var(--color-text-muted)" }}
        >
          {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
        </button>
      </aside>
    </>
  );
}

// StatusBar slim : 3 indicateurs sous forme de pastilles colorées + tooltips,
// utilisateur + logout à droite, horodatage relatif (hover = exact ISO).
// Mobile : un bouton hamburger ouvre le drawer de la sidebar.
function StatusBar({ onMenuOpen }) {
  const { t } = useTranslation(["auth"]);
  const { user, logout } = useAuth();
  const now = new Date().toISOString();

  return (
    <div
      className="flex items-center gap-2 sm:gap-4 px-3 sm:px-4 py-1.5 text-xs border-b"
      style={{
        background: "var(--color-surface)",
        borderColor: "var(--color-border)",
        color: "var(--color-text-muted)",
      }}
    >
      <button
        onClick={onMenuOpen}
        aria-label={t("status.open_navigation")}
        data-testid="mobile-menu-toggle"
        className="md:hidden p-1 -ml-1 rounded hover:bg-white/5 shrink-0"
        style={{ color: "var(--color-accent)" }}
      >
        <Menu size={18} />
      </button>

      <span className="flex items-center gap-1.5" title={t("status.system_nominal")}>
        <span className="inline-block w-2 h-2 rounded-full bg-green-400 animate-pulse" />
        <span className="hidden md:inline">{t("status.system_nominal")}</span>
      </span>
      <span title={t("status.lstm_active")} className="hidden sm:flex items-center gap-1">
        <Brain size={11} color="#00d4ff" /><span className="hidden lg:inline">LSTM</span>
      </span>
      <span title={t("status.gnn_active")} className="hidden sm:flex items-center gap-1">
        <Network size={11} color="#00d4ff" /><span className="hidden lg:inline">GNN</span>
      </span>
      <span title={t("status.shadow_active")} className="hidden sm:flex items-center gap-1">
        <Eye size={11} color="#a78bfa" /><span className="hidden lg:inline">Shadow</span>
      </span>

      {user && (
        <span className="ml-auto flex items-center gap-2 sm:gap-3 min-w-0">
          <span title={t("status.user_uuid", { uuid: user.sub })} className="hidden sm:inline truncate">
            <span style={{ color: "#94a3b8" }}>{user.sub?.slice(0, 8)}</span>
            <span className="mx-1" style={{ color: "#475569" }}>·</span>
            <span style={{ color: user.role === "admin" ? "#fbbf24" : "#60a5fa" }}>
              {user.role}
            </span>
          </span>
          <ScopeSelector />
          <LanguageSwitcher compact />
          <Link
            to="/profile"
            className="flex items-center gap-1 hover:text-cyan-400 transition-colors shrink-0"
            title={t("profile.title")}
          >
            <UserCircle size={14} />
            <span className="hidden sm:inline">{t("profile.title")}</span>
          </Link>
          <button
            onClick={logout}
            className="flex items-center gap-1 hover:text-red-400 transition-colors shrink-0"
            title={t("logout.title")}
          >
            <LogOut size={12} />
            <span className="hidden sm:inline">{t("logout.button")}</span>
          </button>
          <span className="hidden md:inline"><RelativeTime iso={now} /></span>
        </span>
      )}
    </div>
  );
}

// Topology sera bientôt un module 2D dédié — pour l'instant on délègue
// à Console 3D qui contient déjà la vue.
const TopologyModule = Console3DModule;

function Shell() {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const { user, scopeOverride } = useAuth();
  // Le scope effectif voyage dans les en-têtes `X-Mssp-Scope` /
  // `X-Tenant-Scope`, PAS dans l'URL ni les query params. Un module qui reste
  // monté pendant une bascule tenant A → B garde donc une URL et des params
  // identiques : rien n'invalide ses données, et il affiche celles de A sous
  // B jusqu'au poll suivant — indéfiniment si le premier poll de B échoue,
  // puisque l'échec « même identité » conserve les lignes (revue PR #486 P1).
  //
  // Le scope fait partie de l'identité de TOUTE lecture, donc la garde est
  // posée ici plutôt que dans `useApiList` : elle couvre aussi les modules qui
  // fetchent à la main (`ip-detail`, `trusted-sources`, `arbiter`…) et ceux
  // qui gardent de l'état local dérivé. Changer la `key` démonte le sous-arbre :
  // invalidation synchrone, requêtes en vol annulées par les cleanups, refetch
  // immédiat avec les nouveaux en-têtes. Les filtres vivent dans l'URL et
  // survivent donc au remount.
  const scopeKey = scopeKeyOf(user, scopeOverride);

  // Ferme le drawer à chaque navigation (mobile UX standard).
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  return (
    <div className="flex h-screen overflow-hidden" style={{ background: "var(--color-bg)" }}>
      <Sidebar
        collapsed={collapsed}
        onToggle={() => setCollapsed((c) => !c)}
        mobileOpen={mobileOpen}
        onMobileClose={() => setMobileOpen(false)}
      />
      <div className="flex flex-col flex-1 overflow-hidden min-w-0">
        <StatusBar onMenuOpen={() => setMobileOpen(true)} />
        <main className="flex-1 overflow-auto p-3 sm:p-4 md:p-6">
          <Suspense fallback={<ModuleSpinner />}>
            <Routes key={scopeKey}>
              {/* Live Ops */}
              <Route path="/"            element={<Overview />} />
              <Route path="/anomalies"   element={<ADNModule />} />
              <Route path="/flows"       element={<FlowsModule />} />
              <Route path="/incidents"   element={<IncidentsModule />} />
              <Route path="/novelty-candidates" element={<NoveltyCandidatesModule />} />
              <Route path="/campaigns"   element={<CampaignsModule />} />
              <Route path="/topology"    element={<TopologyModule />} />
              <Route path="/shadow"      element={<ShadowDashboard />} />
              <Route path="/ip/:ip"      element={<IpDetailModule />} />

              {/* Governance */}
              <Route path="/honey-data"  element={<HoneyDataModule />} />
              <Route path="/war-mode"    element={<WarModeModule />} />
              <Route path="/models"      element={<Models />} />
              <Route path="/trusted"     element={<TrustedSourcesModule />} />
              <Route path="/baselines"   element={<BaselinesModule />} />
              <Route path="/family-thresholds" element={<FamilyThresholdsModule />} />
              <Route path="/judge-config" element={<JudgeConfigModule />} />
              <Route path="/shadow-review" element={<ShadowReviewModule />} />
              <Route path="/arbiter" element={<ArbiterModule />} />
              <Route path="/fleet-shifts" element={<FleetShiftsModule />} />
              <Route path="/host-baseline-policy" element={<HostBaselinePolicyModule />} />
              <Route path="/probe-setup" element={<ProbeSetupModule />} />
              <Route path="/users" element={<UsersModule />} />

              {/* Lab */}
              <Route path="/post-quantum" element={<PostQuantumModule />} />
              <Route path="/lstm-gnn"    element={<LSTMGNNWasmModule />} />
              <Route path="/entropy"     element={<EntropyAnalyzerModule />} />
              <Route path="/red-team"    element={<RedTeamModule />} />
              <Route path="/sbom"        element={<SBOMModule />} />
              <Route path="/federated"   element={<FederatedLearningModule />} />
              <Route path="/model-health" element={<ModelHealthModule />} />
              <Route path="/cost-budget"  element={<CostBudgetModule />} />
              <Route path="/suppression-suggestions" element={<SuppressionSuggestionsModule />} />

              {/* Account */}
              <Route path="/profile"     element={<Profile />} />

              {/* Compat — anciennes routes (les bookmarks existants survivent) */}
              <Route path="/console-3d"  element={<Navigate to="/topology" replace />} />

              {/* 404 → Overview */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
        </main>
      </div>
    </div>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route
          path="/*"
          element={
            <ProtectedRoute>
              <Shell />
            </ProtectedRoute>
          }
        />
      </Routes>
    </AuthProvider>
  );
}
